import type React from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { isPlaygroundSurface } from '../config/deployment';
import { useOptionalConfig } from '../config/unified';
import { ChatStorageService } from '../services/chatStorage';
import { dispatchHostAction, isHostActionHref } from '../utils/web/hostActions';
import { convertUrlsToMarkdown } from '../utils/web/urlUtils';

interface MarkdownRendererProps {
    content: string;
    style?: React.CSSProperties;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, style }) => {
    // Convert plain URLs to markdown links for better user experience
    const processedContent = convertUrlsToMarkdown(content);
    const resolver = useOptionalConfig()?.resolver ?? null;
    const overrideDomain = resolver?.domain ?? window.location.hostname;
    const intercomFallbackUrl = resolver?.integrations?.intercom?.fallback_url;

    return (
        <div className="force-line-height-28" style={style}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                // Let the `rose-action:` host-action scheme through the URL sanitizer, which
                // would otherwise strip it to an empty href.
                urlTransform={(url) => (isHostActionHref(url) ? url : defaultUrlTransform(url))}
                components={{
                    // Custom styling for code blocks
                    pre: ({ children }) => (
                        <pre
                            style={{
                                backgroundColor: 'var(--rw-code-surface)',
                                padding: '12px',
                                borderRadius: '8px',
                                margin: '8px 0',
                                overflow: 'auto',
                                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                                fontSize: '13px',
                                lineHeight: '1.4',
                                border: '1px solid var(--rw-code-border)',
                            }}
                        >
                            {children}
                        </pre>
                    ),
                    // Custom styling for inline code
                    code: ({ children, ...props }) => (
                        <code
                            style={{
                                backgroundColor: 'var(--rw-code-surface)',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                                fontSize: '13px',
                            }}
                            {...props}
                        >
                            {children}
                        </code>
                    ),
                    // Custom styling for links — same-origin navigate in place, external open new tab
                    a: ({ children, href, ...props }) => {
                        const isHostAction = href ? isHostActionHref(href) : false;
                        const isSameOrigin = (() => {
                            try {
                                return href
                                    ? new URL(href, window.location.origin).origin ===
                                          window.location.origin
                                    : false;
                            } catch {
                                return false;
                            }
                        })();
                        // On playground/preview surfaces the widget shares the origin with the
                        // preview page, so same-origin ("local content") links must open in a
                        // new tab — copilot in-place navigation would replace the playground.
                        const navigateInPlace = isSameOrigin && !isPlaygroundSurface();
                        const isInPlace = isHostAction || navigateInPlace;
                        return (
                            <a
                                href={href}
                                target={isInPlace ? '_self' : '_blank'}
                                rel={isInPlace ? undefined : 'noopener noreferrer'}
                                style={{ color: 'var(--rw-link)', textDecoration: 'underline' }}
                                onClick={
                                    isHostAction
                                        ? (e) => {
                                              e.preventDefault();
                                              if (href) {
                                                  dispatchHostAction(href, {
                                                      trustedParams: {
                                                          intercom: {
                                                              fallback: intercomFallbackUrl ?? '',
                                                          },
                                                      },
                                                  });
                                              }
                                          }
                                        : navigateInPlace
                                          ? (e) => {
                                                e.preventDefault();
                                                ChatStorageService.setCopilotModeOverride(
                                                    overrideDomain,
                                                    true
                                                );
                                                if (href) {
                                                    window.location.href = href;
                                                }
                                            }
                                          : undefined
                                }
                                {...props}
                            >
                                {children}
                            </a>
                        );
                    },
                    // Custom styling for horizontal rules
                    hr: () => (
                        <hr
                            style={{
                                border: 'none',
                                height: 1,
                                backgroundColor: 'var(--rw-code-border)',
                                margin: '12px 0',
                            }}
                        />
                    ),
                    // Custom styling for paragraphs
                    p: ({ children }) => (
                        <div style={{ marginBottom: '16px', lineHeight: '28px !important' }}>
                            {children}
                        </div>
                    ),
                    // Custom styling for unordered lists
                    ul: ({ children }) => (
                        <ul
                            style={{
                                marginLeft: '16px',
                                listStyleType: 'disc',
                                marginBottom: '12px',
                            }}
                        >
                            {children}
                        </ul>
                    ),
                    // Custom styling for ordered lists
                    ol: ({ children }) => (
                        <ol style={{ marginLeft: '20px', marginBottom: '12px' }}>{children}</ol>
                    ),
                    // Custom styling for list items
                    li: ({ children }) => <li style={{ marginBottom: '0' }}>{children}</li>,
                    // Custom styling for tables
                    table: ({ children }) => (
                        <table
                            style={{
                                borderCollapse: 'collapse',
                                width: '100%',
                                marginBottom: '12px',
                                border: '1px solid var(--rw-code-border)',
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}
                        >
                            {children}
                        </table>
                    ),
                    thead: ({ children }) => <thead>{children}</thead>,
                    tbody: ({ children }) => <tbody>{children}</tbody>,
                    tr: ({ children }) => (
                        <tr style={{ borderBottom: '1px solid var(--rw-code-border)' }}>
                            {children}
                        </tr>
                    ),
                    th: ({ children }) => (
                        <th
                            style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600',
                                borderRight: '1px solid var(--rw-code-border)',
                                backgroundColor: 'var(--rw-bg-table-header)',
                                color: 'var(--rw-text-primary)',
                            }}
                        >
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td
                            style={{
                                padding: '12px 16px',
                                borderRight: '1px solid var(--rw-code-border)',
                                verticalAlign: 'top',
                            }}
                        >
                            {children}
                        </td>
                    ),
                }}
            >
                {processedContent}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownRenderer;
